/**
 * ShoppingBasketType for public shop and OmaElisa
 */
export type ShoppingBasketType = {
  /**
   * Unique identifier for the user's shopping basket
   */
  id?: string;
  /**
   * List of items in the shopping basket
   */
  items?: BasketItem[];
  /**
   * Unix timestamp in milliseconds of creation
   */
  created?: number;
  /**
   * Unix timestamp in milliseconds of modification
   */
  modified?: number;
  /**
   * User given active vouchers (when added)
   */
  vouchers?: string[];
};

export type BasketItem = {
  /**
   * Unique identifier for the item
   */
  id: string;
  /**
   * Salesforce unique identifier for the model
   */
  guid: string;
  /**
   * Image url for the selected offer / variant (Note: only used if product is discontinued)
   */
  imageUrl: string;
  /**
   * Name of the product (Note: only used if product is discontinued)
   */
  name: string;
  /**
   * Selected offer of the product
   */
  offer: OfferItem;
  /**
   * Source where the product was added to the basket (Note: will be mandatory in new checkout)
   */
  source?: 'PUBLIC_PAGE' | 'OMAELISA';
  /**
   * Url to the product (Note: only used if product is discontinued)
   */
  pageUrl?: string;
  /**
   * Quantity of the products in the basket
   */
  quantity: number;
};

export type OfferItemAddon = {
  /**
   * Add-on guids for the product
   */
  addOnCodes?: string[];
  /**
   * Attributes for add-ons (e.g. voicemail language). Keyed by add-on code.
   */
  addOnAttributes?: { [addOnCode: string]: { [key: string]: string } };
};

/**
 * Selected offer of the product
 */
export type OfferItem = {
  /**
   * Offer guid
   */
  guid: string;
  /**
   * Onetime price in cents (when added to basket)
   */
  oneTimePriceInCents?: number;
  /**
   * Periodic price on product (when added to basket)
   */
  periodicPrice?: ItemPeriodicPrice;
  /**
   * Add-ons for each product in the basket. Index corresponds to product position.
   */
  addOns?: OfferItemAddon[];
  /**
   * Commercial product guid
   */
  commercialProductGuid: string;
};

/**
 * Periodic price on product (when added to basket)
 */
export type ItemPeriodicPrice = {
  /**
   * Amount of payments, 0 for continuous payment
   */
  payments?: number;
  /**
   * Payment period in months
   */
  period?: number;
  /**
   * Periodic price in cents
   */
  periodicPriceInCents?: number;
};

/**
 * Source where the product was added to the basket
 */
export const BasketItemSource = {
  PUBLIC_PAGE: 'PUBLIC_PAGE' as const,
  OMAELISA: 'OMAELISA' as const,
};
